import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/ui/Button';

/**
 * LimiteDeErrores — que un fallo al pintar una pantalla React se vea.
 *
 * Sin esto, un error durante el render —un endpoint que responde 200 con otra
 * forma, o un fallo de programación— hace que React desmonte el árbol entero y
 * deje el contenedor VACÍO: sin mensaje y sin botón; solo la consola lo sabe.
 * Demostrado el 2026-09-11 con `/kudos/badges` devolviendo `{error}` en vez de
 * un array, y medido otra vez el 2026-09-17 antes de escribir esto: 0 hijos en
 * la raíz, ningún texto, `TypeError: r.map is not a function`. El 2026-09-16
 * pasó además sin buscarlo, con un `ui/Input` fuera de su `Field`.
 *
 * El `try/catch` de `navigate()` no lo cubre: envuelve el montaje, que es
 * síncrono, y el error llega después, cuando vuelven los datos.
 *
 * **Lo que NO captura**, porque React no lo pasa por aquí: los errores de los
 * manejadores de eventos y del código asíncrono. Esos siguen su camino —las
 * consultas los convierten en `isError` y cada pantalla pinta su `EstadoError`—.
 *
 * Va POR FUERA de `Providers`. Así «Reintentar» vuelve a montar también el
 * cliente de consultas, que vive en su estado: se pide todo de nuevo en vez de
 * repintar con los mismos datos que acaban de romper la pantalla.
 *
 * Es una clase porque React no ofrece otra forma: no hay hook equivalente a
 * `getDerivedStateFromError`.
 */
export class LimiteDeErrores extends Component<
  { pantalla: string; children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // Con el nombre de la pantalla: la traza de un bundle minificado no lo dice.
    console.error(`[PortalReact] «${this.props.pantalla}» falló al pintarse:`, error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <i className="bi bi-exclamation-octagon text-9 text-muted opacity-60" aria-hidden="true" />
        {/* `role="alert"` solo en el título, como en `EstadoError`: el detalle
            técnico no es para leérselo a nadie en voz alta. */}
        <p className="text-5 font-semibold text-ink" role="alert">Esta pantalla no se pudo mostrar</p>
        <p className="max-w-prose text-3 leading-relaxed text-muted">
          Es un fallo del portal, no de tu conexión. Vuelve a intentarlo; si se repite, repórtalo con
          el detalle técnico de abajo.
        </p>

        <div className="mt-1 flex flex-wrap justify-center gap-2">
          <Button variant="primary" onClick={() => this.setState({ error: null })}>
            Reintentar
          </Button>
          <Button variant="ghost" onClick={() => APP.navigate('dashboard')}>
            Ir al inicio
          </Button>
        </div>

        <details className="mt-2 max-w-prose text-left">
          <summary className="cursor-pointer text-2 text-muted">Detalle técnico</summary>
          <code className="mt-1 block break-words rounded-xs bg-sunken p-2 text-1 text-ink-2">
            {this.props.pantalla} · {error.message}
          </code>
        </details>
      </div>
    );
  }
}
